import type { Room } from 'livekit-client';
import { liveAvatarSpeakSessionLimitText } from './liveAvatarControl';
import { reportClientRca } from './reportClientRca';

export type LiveAvatarSessionPolicy = {
  sessionMaxMinutes: number;
  sessionWarnMinutes: number;
};

/** Start limit goodbye this many ms before mint cap (LA still in room). */
export const LIVEAVATAR_SESSION_CLOSING_MS = 10_000;
/**
 * After mint cap, if LiveKit never disconnects, force summary/hangup (safety only).
 * Normal path: LA max_session_duration tears down the room → onDisconnected.
 */
export const LIVEAVATAR_SESSION_CAP_SAFETY_MS = 45_000;
/** When LA leaves within this window of the hard cap, treat as session-limit teardown. */
export const LIVEAVATAR_SESSION_LIMIT_LA_LEAVE_MS = 5_000;

/** @deprecated No longer used for LA limit teardown (goodbye runs in closing window). */
export const LIVEAVATAR_SESSION_GOODBYE_GRACE_MS = 2_500;

function sessionLimitWarnText(warnMinutes: number): string {
  const leadM = Math.max(1, Math.round(warnMinutes));
  const leadPhrase =
    leadM <= 1
      ? 'less than a minute left on this session'
      : `about ${leadM} minutes left on this session`;
  return (
    'By the way, I should mention that our time together is almost up — ' +
    `${leadPhrase}. Feel free to call us again anytime.`
  );
}

const SESSION_LIMIT_GOODBYE =
  "I'm sorry, but we've reached the end of this session. " +
  'Thank you for talking with me today — please call again whenever you\'re ready. Goodbye!';

export type LiveAvatarSessionLimitHandle = {
  endsAtMs: number;
  dispose: () => void;
  /** Call when the LiveAvatar remote participant leaves (FULL LaRoom). */
  onLiveAvatarParticipantLeft: () => void;
};

export type LiveAvatarSessionLimitOpts = {
  sessionId?: string;
  /** Mute user mic for closing (no UI toggle); called once when entering closing window. */
  onEnterClosing?: () => void;
};

/** Delay before closing window; lead is capped by time left on the session. */
export function sessionLimitClosingDelayMs(
  msUntilEnd: number,
  closingLeadMs = LIVEAVATAR_SESSION_CLOSING_MS,
): { closingDelayMs: number; closingLeadMs: number } {
  const until = Math.max(0, msUntilEnd);
  const lead = Math.min(closingLeadMs, Math.max(0, until - 500));
  return { closingDelayMs: Math.max(0, until - lead), closingLeadMs: lead };
}

/**
 * Client-side warn + goodbye for LiveAvatar FULL (mirrors Cascade session_limit copy).
 * LA platform enforces max_session_duration from mint — use server `endsAtMs` when provided.
 *
 * Goodbye fires in the closing window (default T−10s) while LA is still connected.
 * Teardown UX is driven by LiveKit disconnect at the cap, not a short client grace timer.
 */
export function startLiveAvatarSessionLimitTimers(
  room: Room,
  policy: LiveAvatarSessionPolicy,
  onHardEnd: () => void,
  endsAtMs: number,
  opts?: LiveAvatarSessionLimitOpts,
): LiveAvatarSessionLimitHandle {
  const sessionId = opts?.sessionId || '';
  const maxM = Math.max(3, Math.min(30, Math.round(policy.sessionMaxMinutes || 15)));
  const warnM = Math.max(1, Math.min(maxM - 1, Math.round(policy.sessionWarnMinutes || 1)));
  const warnLeadMs = warnM * 60_000;
  const limitEndsAtMs = Number.isFinite(endsAtMs) && endsAtMs > 0
    ? endsAtMs
    : Date.now() + maxM * 60_000;

  let warnTimer = 0;
  let closingTimer = 0;
  let capSafetyTimer = 0;
  let finishSafetyTimer = 0;
  let ended = false;
  let goodbyeStarted = false;
  let closingEntered = false;

  const logRca = (type: string, meta?: Record<string, unknown>, message?: string) => {
    if (!sessionId) return;
    reportClientRca(sessionId, type, meta, message);
  };

  const msUntilEnd = Math.max(0, limitEndsAtMs - Date.now());
  const warnDelayMs = Math.max(0, msUntilEnd - warnLeadMs);
  const warnAtMs = Date.now() + warnDelayMs;
  const { closingDelayMs, closingLeadMs } = sessionLimitClosingDelayMs(msUntilEnd);
  const roomState = () => String(room?.state || '');

  logRca('session_limit_timers_started', {
    limitEndsAtMs,
    warnAtMs,
    warnDelayMs,
    warnLeadMs,
    closingDelayMs,
    closingLeadMs,
    sessionMaxMinutes: maxM,
    sessionWarnMinutes: warnM,
    roomState: roomState(),
  });

  const finish = (branch: string) => {
    if (ended) return;
    ended = true;
    logRca('session_limit_hard_end', {
      branch,
      limitEndsAtMs,
      msLeft: limitEndsAtMs - Date.now(),
      roomState: roomState(),
      goodbyeStarted,
      closingEntered,
    });
    onHardEnd();
  };

  const scheduleSafetyFinish = (delayMs: number, branch: string) => {
    if (finishSafetyTimer) window.clearTimeout(finishSafetyTimer);
    finishSafetyTimer = window.setTimeout(() => finish(branch), Math.max(0, delayMs));
  };

  const speakLimitLine = async (phase: 'warn' | 'goodbye', text: string) => {
    const okType = phase === 'warn' ? 'session_limit_warn_ok' : 'session_limit_goodbye_ok';
    const failType = phase === 'warn' ? 'session_limit_warn_failed' : 'session_limit_goodbye_failed';
    logRca(phase === 'warn' ? 'session_limit_warn_fired' : 'session_limit_goodbye_fired', {
      limitEndsAtMs,
      msLeft: limitEndsAtMs - Date.now(),
      roomState: roomState(),
      textLen: text.length,
    });
    try {
      await liveAvatarSpeakSessionLimitText(room, text);
      logRca(okType, { limitEndsAtMs, roomState: roomState() });
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      logRca(failType, { limitEndsAtMs, roomState: roomState() }, message);
    }
  };

  const startGoodbye = () => {
    if (goodbyeStarted) return;
    goodbyeStarted = true;
    void speakLimitLine('goodbye', SESSION_LIMIT_GOODBYE);
  };

  const enterClosing = () => {
    if (closingEntered) return;
    closingEntered = true;
    logRca('session_limit_closing_entered', {
      limitEndsAtMs,
      msLeft: limitEndsAtMs - Date.now(),
      closingLeadMs,
      roomState: roomState(),
    });
    try {
      opts?.onEnterClosing?.();
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      logRca('session_limit_closing_enter_failed', { limitEndsAtMs }, message);
    }
    startGoodbye();
  };

  warnTimer = window.setTimeout(() => {
    void speakLimitLine('warn', sessionLimitWarnText(warnM));
  }, warnDelayMs);

  closingTimer = window.setTimeout(enterClosing, closingDelayMs);

  capSafetyTimer = window.setTimeout(() => {
    logRca('session_limit_cap_reached', {
      limitEndsAtMs,
      msLeft: limitEndsAtMs - Date.now(),
      roomState: roomState(),
      goodbyeStarted,
    });
    scheduleSafetyFinish(LIVEAVATAR_SESSION_CAP_SAFETY_MS, 'cap_safety');
  }, msUntilEnd);

  const onLiveAvatarParticipantLeft = () => {
    const msLeft = limitEndsAtMs - Date.now();
    logRca('session_limit_la_participant_left', {
      msLeft,
      limitEndsAtMs,
      goodbyeStarted,
      closingEntered,
      roomState: roomState(),
    });
    if (msLeft > LIVEAVATAR_SESSION_LIMIT_LA_LEAVE_MS) return;
    // LA left at/near cap — wait for room Disconnected (or cap_safety), do not rush summary.
  };

  const dispose = () => {
    if (warnTimer) window.clearTimeout(warnTimer);
    if (closingTimer) window.clearTimeout(closingTimer);
    if (capSafetyTimer) window.clearTimeout(capSafetyTimer);
    if (finishSafetyTimer) window.clearTimeout(finishSafetyTimer);
    logRca('session_limit_timers_disposed', {
      limitEndsAtMs,
      ended,
      goodbyeStarted,
      closingEntered,
    });
  };

  return { endsAtMs: limitEndsAtMs, dispose, onLiveAvatarParticipantLeft };
}

/** Seconds remaining for UI (hits 0:00 in the last full second before cap). */
export function sessionLimitRemainingSec(endsAtMs: number, nowMs = Date.now()): number {
  return Math.max(0, Math.floor((endsAtMs - nowMs) / 1000));
}
